/**
 * Chapter Name Mappings & Bidirectional Translation
 * 
 * Maps legacy/backend API chapter names to standardized UI display names
 * and ensures API compatibility when making network requests.
 */

export const CHAPTER_MAPPINGS = [
  {
    id: 1,
    exam: "NEET",
    subject: "Chemistry",
    oldName: "Chapter:20-Alcohol,phenols and Ethers",
    newName: "Chapter:20-Alcohol and Phenols and Ethers",
  },
  {
    id: 2,
    exam: "NEET",
    subject: "Chemistry",
    oldName: "Chapter:21-Aldehydes,ketones and Carboxylic Acids",
    newName: "Chapter:21-Aldehydes and Ketones and Carboxylic Acids",
  },
  {
    id: 3,
    exam: "JEE",
    subject: "Mathematics",
    oldName: "Chapter:21-Limits,Continuity,Differentioability",
    newName: "Chapter:21-Limits and Continuity and Differentioability",
  },
  {
    id: 4,
    exam: "JEE",
    subject: "Chemistry",
    oldName: "Chapter:27-Alcohol,phenols and Ethers",
    newName: "Chapter:27-Alcohol and Phenols and Ethers",
  },
  {
    id: 5,
    exam: "JEE",
    subject: "Chemistry",
    oldName: "Chapter:28-Aldehydes,ketones and Carboxylic Acids",
    newName: "Chapter:28-Aldehydes and Ketones and Carboxylic Acids",
  },
];

const normalizeStr = (str) => {
  if (!str) return "";
  return String(str)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/::/g, ":");
};

/**
 * Maps a chapter name (old API format or raw string) to the standard New Name for UI display.
 * @param {string} chapter - The input chapter string.
 * @returns {string} The standardized new chapter name.
 */
export const mapChapterToNewName = (chapter) => {
  if (!chapter || chapter === "All" || chapter === "all") return chapter || "All";
  const norm = normalizeStr(chapter);

  for (const m of CHAPTER_MAPPINGS) {
    if (norm === normalizeStr(m.oldName) || norm === normalizeStr(m.newName)) {
      return m.newName;
    }
    // Also match without the "Chapter:" prefix
    const oldWithoutPrefix = normalizeStr(m.oldName.replace(/^Chapter\s*:\s*/i, ""));
    const newWithoutPrefix = normalizeStr(m.newName.replace(/^Chapter\s*:\s*/i, ""));
    const currentWithoutPrefix = normalizeStr(chapter.replace(/^Chapter\s*:\s*/i, ""));
    if (currentWithoutPrefix === oldWithoutPrefix || currentWithoutPrefix === newWithoutPrefix) {
      return m.newName;
    }
  }

  return chapter;
};

/**
 * Maps a chapter name (from UI selection) back to the legacy API name expected by backend endpoints.
 * @param {string} chapter - The input chapter string.
 * @returns {string} The legacy API chapter name.
 */
export const mapChapterToApiName = (chapter) => {
  if (!chapter || chapter === "All" || chapter === "all") return chapter || "";
  const norm = normalizeStr(chapter);

  for (const m of CHAPTER_MAPPINGS) {
    if (norm === normalizeStr(m.newName) || norm === normalizeStr(m.oldName)) {
      return m.oldName;
    }
    const oldWithoutPrefix = normalizeStr(m.oldName.replace(/^Chapter\s*:\s*/i, ""));
    const newWithoutPrefix = normalizeStr(m.newName.replace(/^Chapter\s*:\s*/i, ""));
    const currentWithoutPrefix = normalizeStr(chapter.replace(/^Chapter\s*:\s*/i, ""));
    if (currentWithoutPrefix === newWithoutPrefix || currentWithoutPrefix === oldWithoutPrefix) {
      return m.oldName;
    }
  }

  return chapter;
};

/**
 * Formats a chapter title cleanly for UI display (strips leading "Chapter:" prefix).
 * @param {string} chapter - Raw chapter name.
 * @returns {string} Clean human-readable chapter title.
 */
export const getChapterDisplayLabel = (chapter) => {
  if (!chapter || chapter === "All" || chapter === "all") return "All Chapters";
  const newName = mapChapterToNewName(chapter);
  return String(newName).replace(/^Chapter\s*:\s*:?\s*/i, "").trim();
};
